'use client'

import { useState } from 'react'

const ENQUIRY_TYPES = [
  { value: 'contact_form',    label: 'General enquiry' },
  { value: 'support_request', label: 'Technical support' },
  { value: 'sales_enquiry',   label: 'Sales / pricing' },
  { value: 'feature_request', label: 'Feature request' },
  { value: 'sales_enquiry_partnership', label: 'Partnership' },
] as const

type FormState = {
  enquiry_type: string
  name: string
  email: string
  phone: string
  company: string
  subject: string
  message: string
  honeypot: string
}

const INITIAL: FormState = {
  enquiry_type: 'contact_form',
  name: '', email: '', phone: '', company: '', subject: '', message: '', honeypot: '',
}

const CONTACT_INFO = [
  {
    icon: (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
        <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/><polyline points="22,6 12,13 2,6"/>
      </svg>
    ),
    label: 'Email us',
    value: 'info@builderbuddi.com',
  },
  {
    icon: (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="12" cy="12" r="10"/><polyline points="12,6 12,12 16,14"/>
      </svg>
    ),
    label: 'Response time',
    value: 'Within 24 hours on weekdays',
  },
  {
    icon: (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="12" cy="12" r="10"/><path d="M2 12h20M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/>
      </svg>
    ),
    label: 'Available to',
    value: 'Contractors worldwide',
  },
]

function InputRow({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="flex flex-col gap-1 text-sm font-medium text-slate-700">
      {label}
      {children}
    </label>
  )
}

export function MarketingContactSection() {
  const [form, setForm] = useState<FormState>(INITIAL)
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState(false)

  function set<K extends keyof FormState>(key: K, val: FormState[K]) {
    setForm(prev => ({ ...prev, [key]: val }))
  }

  async function submit() {
    if (!form.name.trim() || !form.email.trim() || !form.message.trim()) {
      setError('Please fill in your name, email, and message.')
      return
    }
    setSubmitting(true)
    setError(null)
    try {
      const res = await fetch('/api/contact', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      })
      const payload = await res.json().catch(() => ({})) as { error?: string }
      if (!res.ok) { setError(payload.error ?? 'Could not send right now. Try again in a moment.'); return }
      setSuccess(true)
      setForm(INITIAL)
    } catch {
      setError('Could not send right now. Try again in a moment.')
    } finally {
      setSubmitting(false)
    }
  }

  const fieldClass = 'mt-1 w-full min-h-11 rounded-xl border border-slate-200 bg-white px-3.5 py-2.5 text-[14px] text-slate-900 placeholder:text-slate-400 focus:border-[#1e3a5f] focus:outline-none focus:ring-2 focus:ring-[#1e3a5f]/10 transition'

  return (
    <section id="contact" className="border-t border-slate-100 bg-white py-11 sm:py-24">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="grid items-start gap-8 lg:grid-cols-[1fr_1.3fr] lg:gap-20">

          {/* Left: info */}
          <div>
            <p className="text-xs uppercase tracking-[0.16em] text-[#c2571a] font-semibold mb-3">Get in touch</p>
            <h2 className="mb-3 text-[1.95rem] font-bold leading-tight tracking-tight text-slate-900 sm:mb-4 sm:text-5xl">
              We&apos;re here to help.
            </h2>
            <p className="mb-6 text-[1rem] leading-relaxed text-slate-600 sm:mb-8 sm:text-lg sm:text-slate-500">
              Have a question about BuilderBuddi? Want to see a demo? Or just want to tell us
              what you need? Drop us a message and we will get back to you quickly.
            </p>

            <div className="mb-8 space-y-4 sm:mb-10 sm:space-y-5">
              {CONTACT_INFO.map(item => (
                <div key={item.label} className="flex items-start gap-4">
                  <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-[#1e3a5f]/8 text-[#1e3a5f]">
                    {item.icon}
                  </div>
                  <div>
                    <p className="text-[12px] uppercase tracking-[0.12em] font-semibold text-slate-400 mb-0.5">{item.label}</p>
                    <p className="text-[15px] font-medium text-slate-800">{item.value}</p>
                  </div>
                </div>
              ))}
            </div>

            <div className="rounded-2xl bg-[#0b1520] p-5">
              <p className="text-white font-bold text-[16px] mb-1.5">Already using BuilderBuddi?</p>
              <p className="mb-4 text-sm leading-relaxed text-white/72 sm:text-white/50">
                For support with your account, use the support section inside the app — it routes
                directly to our team and gets faster responses.
              </p>
              <a
                href="/login"
              className="inline-flex min-h-11 items-center gap-2 text-sm font-semibold text-[#c7681f] transition-colors hover:text-[#e07830]"
              >
                Log in to get support
                <svg width="13" height="13" viewBox="0 0 13 13" fill="none">
                  <path d="M2 6.5h9M8 3l3.5 3.5L8 10" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </a>
            </div>
          </div>

          {/* Right: form */}
          <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4 shadow-sm sm:p-8">
            {success ? (
              <div className="flex flex-col items-center justify-center text-center py-10 gap-4">
                <div className="flex h-14 w-14 items-center justify-center rounded-full bg-emerald-100 border border-emerald-200">
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#059669" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M20 6L9 17l-5-5"/>
                  </svg>
                </div>
                <div>
                  <p className="text-lg font-bold text-slate-900 mb-1">Message sent</p>
                  <p className="text-sm text-slate-600 sm:text-slate-500">We will be in touch within 24 hours.</p>
                </div>
                <button
                  onClick={() => setSuccess(false)}
                  className="mt-2 text-sm font-semibold text-[#c2571a] hover:text-[#a84915] transition-colors"
                >
                  Send another message
                </button>
              </div>
            ) : (
              <>
                <h3 className="text-xl font-bold text-slate-900 mb-1">Send us a message</h3>
                <p className="mb-5 text-sm text-slate-600 sm:mb-6 sm:text-slate-500">We route every enquiry directly to the right person.</p>

                <div className="space-y-4">
                  <InputRow label="Enquiry type">
                    <select className={fieldClass} value={form.enquiry_type} onChange={e => set('enquiry_type', e.target.value)}>
                      {ENQUIRY_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                    </select>
                  </InputRow>

                  <div className="grid sm:grid-cols-2 gap-4">
                    <InputRow label="Name *">
                      <input className={fieldClass} placeholder="Your name" value={form.name} onChange={e => set('name', e.target.value)} />
                    </InputRow>
                    <InputRow label="Email *">
                      <input className={fieldClass} type="email" placeholder="you@example.com" value={form.email} onChange={e => set('email', e.target.value)} />
                    </InputRow>
                    <InputRow label="Phone (optional)">
                      <input className={fieldClass} placeholder="+27 ..." value={form.phone} onChange={e => set('phone', e.target.value)} />
                    </InputRow>
                    <InputRow label="Company (optional)">
                      <input className={fieldClass} placeholder="Your business name" value={form.company} onChange={e => set('company', e.target.value)} />
                    </InputRow>
                  </div>

                  <InputRow label="Subject">
                    <input className={fieldClass} placeholder="What's this about?" value={form.subject} onChange={e => set('subject', e.target.value)} />
                  </InputRow>

                  <InputRow label="Message *">
                    <textarea
                      className={`${fieldClass} min-h-[120px] resize-none`}
                      placeholder="Tell us what you need..."
                      value={form.message}
                      onChange={e => set('message', e.target.value)}
                    />
                  </InputRow>

                  {/* Honeypot */}
                  <label className="hidden" aria-hidden>
                    Website
                    <input tabIndex={-1} autoComplete="off" value={form.honeypot} onChange={e => set('honeypot', e.target.value)} />
                  </label>
                </div>

                {error && (
                  <p className="mt-4 rounded-xl border border-red-200 bg-red-50 px-4 py-2.5 text-sm text-red-700">{error}</p>
                )}

                <button
                  type="button"
                  onClick={() => void submit()}
                  disabled={submitting}
                  className="mt-6 inline-flex min-h-14 w-full items-center justify-center gap-2 rounded-xl bg-[#c2571a] px-6 py-3.5 text-base font-semibold text-white shadow-sm transition-all hover:-translate-y-0.5 hover:bg-[#a84915] active:translate-y-0 disabled:cursor-not-allowed disabled:opacity-60"
                >
                  {submitting ? (
                    <>
                      <svg className="animate-spin" width="16" height="16" viewBox="0 0 16 16" fill="none">
                        <path d="M8 2a6 6 0 1 1-4.24 1.76" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round"/>
                      </svg>
                      Sending...
                    </>
                  ) : (
                    <>
                      Send message
                      <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                        <path d="M3 8h10M9 4l4 4-4 4" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    </>
                  )}
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    </section>
  )
}
